// ============================================================================
// components/remote/RemoteAccessSettingsPanel.tsx — ХОСТ-сторона «Удалённого
// доступа»: политика «спрашивать подтверждение» | «подключаться по паролю»
// (unattended, как AnyDesk). Задаёт/меняет пароль удалённого доступа и
// включает/выключает unattended. Управление — ТОЛЬКО роль «Remote-управление»
// (remote_manage) / админ (SSoT — сервер RemoteService; панель показывается
// при canManageRemote). Пароль наружу никогда не отдаётся — только флаг
// «пароль задан». Живое обновление — шина `remote.access.updated`.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Loader2, ShieldCheck, ShieldOff } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { eventBusHub } from '@/lib/eventBusHub';
import { logEvent } from '@/lib/eventLog';
import { friendlyError } from '@/lib/errors';
import { useToastStore } from '@/store/toastStore';

interface RemoteAccessSettings {
  scopeType?: string;
  scopeId?: string;
  mode?: string;
  unattendedEnabled?: boolean;
  passwordSet?: boolean;
  effectiveMode?: string;
  sourceScopeType?: string;
  updatedAt?: string;
}

const MODE_LABELS: Record<string, string> = {
  consent: 'Спрашивать подтверждение',
  password: 'Подключаться по паролю (unattended)',
};

export function RemoteAccessSettingsPanel() {
  const [settings, setSettings] = useState<RemoteAccessSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'consent' | 'password'>('consent');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r: RemoteAccessSettings = await getSDK().remote.getRemoteAccessSettings({});
      setSettings(r);
      setMode(r?.effectiveMode === 'password' || r?.mode === 'password' ? 'password' : 'consent');
      setError('');
    } catch (e) {
      setError(friendlyError(e));
      void logEvent({
        level: 'error',
        category: 'ui',
        message: 'удалённый доступ: не удалось загрузить настройки пароля',
        entityType: 'remote',
        entityCode: 'access',
        details: String((e as Error)?.message || e),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Живое обновление: политика изменена (в т.ч. другим администратором).
  useEffect(() => {
    return eventBusHub.subscribe((ev) => {
      if (ev.topic === 'remote.access.updated') void load();
    });
  }, [load]);

  const save = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      if (mode === 'password' && !password && !settings?.passwordSet) {
        setError('Введите пароль удалённого доступа (минимум 4 символа)');
        return;
      }
      const r: RemoteAccessSettings = await getSDK().remote.setRemoteAccessPassword({
        mode,
        unattendedEnabled: mode === 'password',
        password: password || undefined,
      });
      setSettings(r);
      setPassword('');
      useToastStore.getState().showSuccess(
        mode === 'password' ? 'Вход по паролю включён' : 'Вход по паролю выключен (спрашивать подтверждение)',
      );
      void logEvent({
        level: 'info',
        category: 'ui',
        message: 'удалённый доступ: политика входа изменена',
        entityType: 'remote',
        entityCode: 'access',
        details: JSON.stringify({ mode }),
      });
    } catch (e) {
      const msg = friendlyError(e);
      setError(msg);
      void logEvent({
        level: 'error',
        category: 'ui',
        message: 'удалённый доступ: не удалось сохранить политику входа',
        entityType: 'remote',
        entityCode: 'access',
        details: String((e as Error)?.message || e),
      });
    } finally {
      setBusy(false);
    }
  }, [mode, password, settings?.passwordSet]);

  const revoke = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const r: RemoteAccessSettings = await getSDK().remote.revokeRemoteAccess({});
      setSettings(r);
      setMode('consent');
      setPassword('');
      useToastStore.getState().showSuccess('Пароль удалённого доступа отозван');
      void logEvent({
        level: 'info',
        category: 'ui',
        message: 'удалённый доступ: пароль отозван',
        entityType: 'remote',
        entityCode: 'access',
      });
    } catch (e) {
      const msg = friendlyError(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Загрузка настроек…
      </div>
    );
  }

  const passwordSet = settings?.passwordSet === true;
  const effective = settings?.effectiveMode || 'consent';

  return (
    <div className="mx-auto w-full max-w-2xl p-4">
      <div className="mb-3 flex items-center gap-2">
        {effective === 'password'
          ? <ShieldOff className="h-4 w-4 text-amber-500" />
          : <ShieldCheck className="h-4 w-4 text-primary" />}
        <div className="text-sm font-semibold">Вход на это устройство</div>
      </div>

      <div className="rounded-lg border bg-card p-3">
        <label className="flex cursor-pointer items-start gap-3 py-2">
          <input
            type="radio"
            className="mt-0.5"
            checked={mode === 'consent'}
            onChange={() => setMode('consent')}
            disabled={busy}
          />
          <span>
            <span className="text-sm">{MODE_LABELS.consent}</span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              Подключение возможно только после вашего подтверждения на этом устройстве (безопасный режим по умолчанию).
            </span>
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-3 border-t py-2">
          <input
            type="radio"
            className="mt-0.5"
            checked={mode === 'password'}
            onChange={() => setMode('password')}
            disabled={busy}
          />
          <span>
            <span className="text-sm">{MODE_LABELS.password}</span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              Зная пароль, сотрудник подключается к вам БЕЗ подтверждения. Пароль хранится только хэшем.
              На Windows-хосте (наш движок) работает полноценно; на Android захват экрана всё равно
              требует согласия на сессию.
            </span>
          </span>
        </label>

        {mode === 'password' && (
          <div className="mt-2 border-t pt-3">
            <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
              <KeyRound className="h-3.5 w-3.5" />
              {passwordSet ? 'Пароль задан — можно сменить' : 'Пароль удалённого доступа (минимум 4 символа)'}
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={passwordSet ? '••••••••  (пусто = не менять)' : 'Новый пароль'}
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary"
              disabled={busy}
            />
          </div>
        )}

        {error && (
          <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            {busy ? 'Сохранение…' : 'Сохранить'}
          </button>
          {passwordSet && (
            <button
              type="button"
              onClick={() => void revoke()}
              disabled={busy}
              className="rounded-md border px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              Отозвать пароль
            </button>
          )}
          <span className="ml-auto text-[11px] text-muted-foreground">
            Действует: {MODE_LABELS[effective] || effective}
            {settings?.sourceScopeType && settings.sourceScopeType !== 'none' ? ` · уровень: ${settings.sourceScopeType}` : ''}
          </span>
        </div>
      </div>

      <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-[11px] text-muted-foreground">
        Пароль удалённого доступа задаёт роль «Remote-управление» (remote_manage) или администратор.
        Подключаться по паролю могут обладатели роли «Remote-клиент» (remote_client). Неудачные
        попытки ограничены по частоте и записываются в журнал регистрации.
      </div>
    </div>
  );
}
